/**
 * PDF Generator Utility
 * Konverterer HTML til PDF med jsPDF
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Generer PDF fra HTML-element
 * @param {HTMLElement} element - HTML-elementet som skal konverteres
 * @param {string} filename - Filnavn for PDF (uten .pdf)
 * @param {Object} options - Tilleggsopsjoner
 */
export async function genererPDFFraHTML(element, filename = 'dokument', options = {}) {
  try {
    // Default opsjoner
    const defaultOptions = {
      scale: 2, // Høyere kvalitet
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    };

    const mergedOptions = { ...defaultOptions, ...options };

    // Konverter HTML til canvas
    const canvas = await html2canvas(element, mergedOptions);

    // Beregn dimensjoner (A4-format)
    const imgWidth = 210; // A4 bredde i mm
    const pageHeight = 297; // A4 høyde i mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    // Opprett PDF
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgData = canvas.toDataURL('image/png');

    // Legg til første side
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Legg til flere sider hvis nødvendig
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Last ned PDF
    pdf.save(`${filename}.pdf`);

    return {
      success: true,
      filename: `${filename}.pdf`
    };

  } catch (error) {
    console.error('Feil ved PDF-generering:', error);
    throw new Error(`Kunne ikke generere PDF: ${error.message}`);
  }
}

/**
 * Generer PDF fra HTML-streng
 * @param {string} htmlString - HTML som streng
 * @param {string} filename - Filnavn
 */
export async function genererPDFFraHTMLString(htmlString, filename = 'dokument') {
  // Opprett midlertidig container
  const container = document.createElement('div');
  container.innerHTML = htmlString;
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.width = '210mm'; // A4 bredde
  container.style.padding = '20mm';
  container.style.backgroundColor = '#ffffff';

  document.body.appendChild(container);

  try {
    await genererPDFFraHTML(container, filename);
  } finally {
    // Fjern midlertidig container
    document.body.removeChild(container);
  }
}

/**
 * Åpne PDF i ny fane (for forhåndsvisning)
 * @param {HTMLElement} element - HTML-elementet
 */
export async function apnePDFINyFane(element) {
  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgData = canvas.toDataURL('image/png');

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Åpne i ny fane
    const pdfBlob = pdf.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');

  } catch (error) {
    console.error('Feil ved åpning av PDF:', error);
    throw new Error(`Kunne ikke åpne PDF: ${error.message}`);
  }
}

/**
 * Optimaliser HTML for PDF-konvertering
 * Gjør justeringer for bedre utskrift
 */
export function optimaliserHTMLForPDF(htmlString) {
  // Legg til CSS for utskrift
  const printCSS = `
    <style>
      @media print {
        @page {
          size: A4;
          margin: 20mm;
        }
        body {
          margin: 0;
          padding: 0;
          font-family: Arial, sans-serif;
        }
        .page-break {
          page-break-after: always;
        }
        .no-print {
          display: none !important;
        }
      }
    </style>
  `;

  return printCSS + htmlString;
}
